/**
 * ATHENA'S WAR ROOM - PREMIUM COMMAND CENTER
 * 
 * The ultimate competitive intelligence headquarters.
 * Design Philosophy: Military command center meets premium data visualization
 * 
 * Features:
 * - Dramatic dark theme with scan effects
 * - Interactive competitor battle map
 * - Animated threat radar
 * - Content gap heatmap
 * - Athena's strategic briefings with personality
 */

import React, { useState, useEffect, useRef } from 'react';
import { useWorkflow } from '../../contexts/WorkflowContext';
import { useIntelligence } from '../../contexts/IntelligenceContext';
import { RiverButton, RiverBanner } from '../ui/RiverFlow';

// Animated scanning line effect
const ScanLine = ({ isActive }) => {
  if (!isActive) return null;
  
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div 
        className="absolute h-px w-full bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-75"
        style={{
          animation: 'scanDown 3s ease-in-out infinite',
          boxShadow: '0 0 30px 5px rgba(6, 182, 212, 0.5)'
        }}
      />
      <style>{`
        @keyframes scanDown {
          0%, 100% { top: 0; opacity: 0; }
          10% { opacity: 0.8; }
          90% { opacity: 0.8; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  );
};

// Radar sweep animation
const RadarSweep = ({ size = 200, isActive = true }) => {
  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Radar circles */}
      {[0.25, 0.5, 0.75, 1].map((scale, i) => (
        <div
          key={i}
          className="absolute border border-cyan-500/30 rounded-full"
          style={{
            width: size * scale,
            height: size * scale,
            top: (size - size * scale) / 2,
            left: (size - size * scale) / 2,
          }}
        />
      ))}
      
      {/* Center dot */}
      <div 
        className="absolute w-3 h-3 bg-cyan-400 rounded-full"
        style={{ top: size/2 - 6, left: size/2 - 6 }}
      />
      
      {/* Sweep line */}
      {isActive && (
        <div
          className="absolute origin-center"
          style={{
            width: size/2,
            height: 2,
            top: size/2 - 1,
            left: size/2,
            background: 'linear-gradient(90deg, rgba(6,182,212,0.8) 0%, transparent 100%)',
            animation: 'sweep 4s linear infinite',
            boxShadow: '0 0 20px rgba(6,182,212,0.5)'
          }}
        />
      )}
      
      <style>{`
        @keyframes sweep {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Threat level bar
const ThreatBar = ({ level, label }) => {
  const percentage = level * 20; // 0-5 scale to percentage
  const color = level >= 4 ? 'red' : level >= 3 ? 'amber' : level >= 2 ? 'yellow' : 'emerald';
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-400">{label}</span>
        <span className={`text-${color}-400 font-medium`}>{level}/5</span>
      </div>
      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
        <div 
          className={`h-full bg-gradient-to-r from-${color}-600 to-${color}-400 transition-all duration-1000 rounded-full`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Competitor battle card - enhanced
const CompetitorCard = ({ competitor, index, isSelected, onSelect }) => {
  const threatColors = {
    high: { bg: 'from-red-500/20 to-orange-500/20', border: 'border-red-500/30', text: 'text-red-400' },
    medium: { bg: 'from-amber-500/20 to-yellow-500/20', border: 'border-amber-500/30', text: 'text-amber-400' },
    low: { bg: 'from-emerald-500/20 to-cyan-500/20', border: 'border-emerald-500/30', text: 'text-emerald-400' }
  };
  
  const threat = threatColors[competitor.threat_level] || threatColors.medium;
  
  return (
    <div 
      onClick={() => onSelect(competitor)}
      className={`
        relative group cursor-pointer rounded-xl border transition-all duration-500 overflow-hidden
        ${isSelected 
          ? `bg-gradient-to-br ${threat.bg} ${threat.border} shadow-lg`
          : 'bg-gray-900/50 border-gray-800 hover:border-gray-700'}
      `}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Scan effect on hover */}
      <ScanLine isActive={isSelected} />
      
      <div className="relative p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center text-lg">
                🎯
              </div>
              <h3 className="font-bold text-white">{competitor.name}</h3>
            </div>
            <p className="text-sm text-gray-500">{competitor.domain}</p>
          </div>
          
          <div className={`px-3 py-1 rounded-full text-xs font-bold ${threat.text} bg-gray-800`}>
            {competitor.threat_level?.toUpperCase() || 'MEDIUM'} THREAT
          </div>
        </div>
        
        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="text-center p-2 bg-gray-800/50 rounded-lg">
            <div className="text-lg font-bold text-cyan-400">{competitor.blog_posts || 0}</div>
            <div className="text-xs text-gray-500">Posts</div>
          </div>
          <div className="text-center p-2 bg-gray-800/50 rounded-lg">
            <div className="text-lg font-bold text-purple-400">{competitor.social_presence || 0}</div>
            <div className="text-xs text-gray-500">Social</div>
          </div>
          <div className="text-center p-2 bg-gray-800/50 rounded-lg">
            <div className="text-lg font-bold text-amber-400">{competitor.content_gaps || 0}</div>
            <div className="text-xs text-gray-500">Gaps</div>
          </div>
        </div>
        
        {/* Keywords */}
        {competitor.top_keywords && competitor.top_keywords.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {competitor.top_keywords.slice(0, 4).map((kw, i) => {
              // Handle both string and object keywords
              const kwText = typeof kw === 'string' ? kw : (kw?.keyword || kw?.name || kw?.text || '');
              return (
                <span key={i} className="px-2 py-0.5 bg-gray-800 rounded text-xs text-gray-400">
                  {kwText}
                </span>
              );
            })}
          </div>
        )}
        
        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-3 right-3 w-3 h-3 bg-cyan-400 rounded-full animate-pulse" />
        )}
      </div>
    </div>
  );
};

// Strategic briefing panel with Athena's personality
const AthenasBriefing = ({ analysis, isAnalyzing }) => {
  const [displayText, setDisplayText] = useState('');
  
  // Safely extract briefing text - handle both string and object responses
  const rawBriefing = analysis?.strategic_briefing || analysis?.counter_strategy?.executive_summary || '';
  const briefing = typeof rawBriefing === 'string' ? rawBriefing : 
    (rawBriefing?.text || rawBriefing?.summary || rawBriefing?.message || JSON.stringify(rawBriefing));
  
  // Typewriter effect
  useEffect(() => {
    if (!briefing || typeof briefing !== 'string') {
      setDisplayText('');
      return;
    }
    
    let index = 0;
    setDisplayText('');
    
    const timer = setInterval(() => {
      if (index < briefing.length) {
        setDisplayText(prev => prev + briefing[index]);
        index++;
      } else {
        clearInterval(timer);
      }
    }, 15);
    
    return () => clearInterval(timer);
  }, [briefing]);
  
  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-900 to-amber-950/30 rounded-2xl border border-amber-500/30 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-amber-500/20 bg-gradient-to-r from-amber-500/10 to-transparent">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <span className="text-2xl">🦉</span>
            </div>
            {isAnalyzing && (
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-cyan-400 rounded-full animate-pulse" />
            )}
          </div>
          <div>
            <h3 className="font-bold text-amber-400 text-lg">Athena&apos;s Strategic Briefing</h3>
            <p className="text-xs text-gray-500">Goddess of Wisdom • Competitive Intelligence</p>
          </div>
          {isAnalyzing && (
            <div className="ml-auto flex items-center gap-2">
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-ping" />
              <span className="text-sm text-cyan-400">Analyzing battlefield...</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6">
        {!analysis ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
              <span className="text-3xl">🔮</span>
            </div>
            <p className="text-gray-400 italic text-lg">
              &ldquo;Run a competitive analysis and I shall reveal the battlefield to you.&rdquo;
            </p>
            <p className="text-gray-600 text-sm mt-2">— Athena, Goddess of Strategy</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Main briefing with typewriter */}
            <div className="relative">
              <div className="absolute -left-3 top-0 w-1 h-full bg-gradient-to-b from-amber-500 to-orange-500 rounded-full" />
              <p className="text-gray-300 leading-relaxed pl-4 text-lg">
                {displayText}
                <span className="animate-pulse text-amber-400">|</span>
              </p>
            </div>
            
            {/* Key Insights */}
            {analysis.key_insights && analysis.key_insights.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-800">
                <h4 className="text-xs font-bold text-amber-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <span>⚡</span> Key Insights
                </h4>
                <div className="space-y-3">
                  {analysis.key_insights.map((insight, i) => {
                    // BULLETPROOF: Convert ANY value to string for display
                    const displayText = typeof insight === 'string' ? insight : String(insight || '');
                    return (
                      <div key={i} className="flex items-start gap-3 p-3 bg-gray-800/30 rounded-lg">
                        <span className="text-amber-500 font-bold">{i + 1}.</span>
                        <span className="text-gray-300">{displayText}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Battle Plan */}
            {analysis.recommended_actions && analysis.recommended_actions.length > 0 && (
              <div className="mt-4 p-5 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-xl border border-cyan-500/20">
                <h4 className="text-xs font-bold text-cyan-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <span>🎯</span> Battle Plan
                </h4>
                <ul className="space-y-2">
                  {analysis.recommended_actions.map((action, i) => {
                    // BULLETPROOF: Convert ANY value to string for display
                    const displayText = typeof action === 'string' ? action : String(action || '');
                    return (
                      <li key={i} className="flex items-start gap-2 text-gray-300">
                        <span className="text-cyan-400">→</span>
                        <span>{displayText}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Content gap opportunity matrix - enhanced with scoring
const OpportunityMatrix = ({ gaps, briefs }) => {
  const { startRiver } = useWorkflow();
  if (!gaps || gaps.length === 0) {
    return (
      <div className="bg-gray-900/50 rounded-2xl border border-gray-800 p-8 text-center">
        <div className="text-4xl mb-4">🔍</div>
        <p className="text-gray-400">Run an analysis to discover content gaps</p>
      </div>
    );
  }

  const difficultyColor = (d) => {
    if (d === 'Easy Win') return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    if (d === 'Moderate Effort') return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    return 'bg-red-500/20 text-red-400 border-red-500/30';
  };

  return (
    <div className="space-y-6" data-testid="athena-opportunity-matrix">
      {/* Ready-to-Forge Briefs Section (Auto-feed to Hermes) */}
      {briefs && briefs.length > 0 && (
        <div className="bg-gradient-to-br from-purple-900/30 via-gray-900 to-orange-900/20 rounded-2xl border border-purple-500/30 overflow-hidden" data-testid="ready-to-forge-briefs">
          <div className="px-6 py-4 border-b border-purple-500/20 bg-gradient-to-r from-purple-500/10 to-orange-500/10">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚒️</span>
              <div>
                <h3 className="font-bold text-white text-lg">Ready-to-Forge Briefs</h3>
                <p className="text-xs text-gray-400 mt-0.5">Top gaps pre-packaged and ready to send to Content Forge</p>
              </div>
              <span className="ml-auto px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-bold">
                {briefs.length} BRIEFS
              </span>
            </div>
          </div>
          <div className="p-5 grid gap-4">
            {briefs.map((brief, i) => (
              <div key={i} className="bg-gray-900/60 rounded-xl border border-gray-800 p-5 hover:border-purple-400/40 transition-all" data-testid={`brief-card-${i}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-xs font-bold">BRIEF #{i + 1}</span>
                      <span className="text-xs text-gray-500">Counter-Competitor Piece</span>
                    </div>
                    <h4 className="text-lg font-bold text-white leading-snug mb-1">{brief.suggested_title}</h4>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400"><span className="text-gray-500">Target keyword:</span> <span className="text-cyan-300 font-medium">{brief.target_keyword}</span></span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="text-sm text-gray-300">
                    <span className="text-purple-400 font-semibold">Why it matters: </span>
                    {brief.why_it_matters}
                  </div>
                  <div className="text-sm text-gray-300">
                    <span className="text-orange-400 font-semibold">Competitive context: </span>
                    {brief.competitive_context}
                  </div>
                </div>
                <RiverButton
                  label="Send to Content Forge"
                  icon="⚒️"
                  onClick={() => startRiver({
                    topic: brief.suggested_title,
                    keywords: brief.target_keyword,
                    contentAngle: 'counter_competitor',
                    competitorGap: brief.topic,
                    targetTool: 'generate',
                    sourceTool: 'athena',
                    sourceGod: 'Athena',
                  })}
                  className="!text-sm"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Gaps with Scoring */}
      <div className="bg-gray-900/50 rounded-2xl border border-gray-800 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-white">Content Gap Opportunities</h3>
            <p className="text-xs text-gray-500">Ranked by opportunity score (1-100)</p>
          </div>
          <span className="text-sm text-cyan-400 font-medium">{gaps.length} found</span>
        </div>

        <div className="divide-y divide-gray-800/50">
          {gaps.slice(0, 12).map((gap, i) => {
            const displayText = typeof gap === 'string' ? gap :
              (typeof gap?.topic === 'string' ? gap.topic : String(gap?.topic || gap?.title || `Gap ${i + 1}`));
            const score = typeof gap?.opportunity_score === 'number' ? gap.opportunity_score : (typeof gap?.score === 'number' ? gap.score : (90 - i * 5));
            const difficulty = gap?.difficulty || (score >= 75 ? 'Easy Win' : score >= 50 ? 'Moderate Effort' : 'Hard Battle');
            const impact = gap?.projected_impact || '';
            const quality = gap?.quality_rating;

            return (
              <div key={i} className="px-6 py-5 hover:bg-gray-800/30 transition-colors group" data-testid={`gap-row-${i}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-lg font-bold text-gray-600">#{i + 1}</span>
                    <span className="font-semibold text-white group-hover:text-cyan-400 transition-colors truncate">
                      {displayText}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${difficultyColor(difficulty)}`}>
                      {difficulty}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold
                      ${score >= 80 ? 'bg-emerald-500/20 text-emerald-400' :
                        score >= 60 ? 'bg-amber-500/20 text-amber-400' :
                        'bg-gray-700 text-gray-400'}
                    `}>
                      {score}/100
                    </span>
                  </div>
                </div>

                {/* Opportunity bar */}
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden mb-3">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 transition-all duration-1000"
                    style={{ width: `${score}%` }}
                  />
                </div>

                {/* Projected Impact */}
                {impact && (
                  <div className="mb-2 flex items-start gap-2 text-sm">
                    <span className="text-emerald-400 font-semibold flex-shrink-0">📈 Projected Impact:</span>
                    <span className="text-gray-300">{impact}</span>
                  </div>
                )}

                {/* Competitor Quality Rating */}
                {quality && (
                  <div className="mb-2 flex items-start gap-2 text-sm">
                    <span className={`flex-shrink-0 font-semibold ${quality.score < 50 ? 'text-red-400' : quality.score < 65 ? 'text-amber-400' : 'text-gray-300'}`}>
                      ⚖️ Competitor Quality: {quality.score}/100
                    </span>
                    <span className="text-gray-400">— {(quality.issues || []).join(', ')}</span>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-2 opacity-80 group-hover:opacity-100 transition-opacity">
                  <RiverButton
                    label="Create Counter-Content"
                    icon="⚔️"
                    onClick={() => startRiver({
                      topic: displayText,
                      keywords: displayText,
                      contentAngle: 'counter_competitor',
                      competitorGap: displayText,
                      targetTool: 'generate',
                      sourceTool: 'athena',
                      sourceGod: 'Athena',
                    })}
                    className="!py-1.5 !px-3 !text-xs"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Analysis depth options with credit costs
const ANALYSIS_DEPTHS = [
  { id: 'quick', name: 'Quick Scan', credits: 10, description: 'Fast overview of competitors' },
  { id: 'standard', name: 'Standard', credits: 15, description: 'Balanced analysis with key insights' },
  { id: 'deep', name: 'Deep Scan', credits: 30, description: 'Comprehensive competitive intelligence' },
  { id: 'comprehensive', name: 'Full Intel', credits: 45, description: 'Complete market domination report' }
];

// Main War Room Component
const AthenaWarRoom = ({ API, user, onRunAgent, onBack }) => {
  const { workflow } = useWorkflow();
  const { storeCompetitorData } = useIntelligence();
  const [analysis, setAnalysis] = useState(null);
  const [enhancedGaps, setEnhancedGaps] = useState(null);
  const [readyBriefs, setReadyBriefs] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showConfig, setShowConfig] = useState(false);
  const [selectedDepth, setSelectedDepth] = useState('standard');
  const [industry, setIndustry] = useState('technology');
  const [focusAreas, setFocusAreas] = useState(['content marketing', 'SEO']);
  
  // Handle running Athena analysis
  const handleRunAnalysis = () => {
    if (onRunAgent) {
      onRunAgent('competitor_spy', {
        analysis_depth: selectedDepth,
        industry: industry,
        focus_areas: focusAreas
      });
    } else {
      setIsAnalyzing(true);
    }
  };
  
  // Fetch existing analyses on mount
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Get outputs from Athena agents
        const agentsResponse = await fetch(`${API}/agents`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (agentsResponse.ok && isMounted) {
          const agentsData = await agentsResponse.json();
          const athenaAgents = (agentsData.agents || []).filter(
            a => a.agent_type === 'competitor_spy'
          );
          
          for (const athena of athenaAgents) {
            const outputsResponse = await fetch(`${API}/agents/${athena.id}/outputs?limit=1`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (outputsResponse.ok && isMounted) {
              const outputsData = await outputsResponse.json();
              const latestOutput = outputsData.outputs?.[0];
              
              if (latestOutput?.output_data) {
                const report = latestOutput.output_data;
                
                // Safely extract key insights as strings (handle both field names)
                const rawOpportunities = report.opportunities?.ranked_opportunities || report.opportunities?.top_opportunities || [];
                const keyInsights = rawOpportunities.slice(0, 3).map(opp => {
                  if (typeof opp === 'string') return opp;
                  return opp?.title || opp?.topic || opp?.description || opp?.text || 
                    (typeof opp === 'object' ? `${opp?.gap_type || 'Opportunity'}: ${opp?.topic || opp?.title || 'Strategic gap identified'}` : String(opp));
                });
                
                // Safely extract recommended actions as strings
                const rawTopics = report.topic_recommendations?.topics || [];
                const recommendedActions = rawTopics.slice(0, 3).map(topic => {
                  if (typeof topic === 'string') return topic;
                  return topic?.title || topic?.topic || topic?.description || 
                    (typeof topic === 'object' ? `${topic?.suggested_format || 'Content'}: ${topic?.title || 'Strategic topic'}` : String(topic));
                });
                
                // Safely extract content gaps as structured objects with string properties
                const rawGaps = report.content_gaps?.gaps || [];
                const contentGaps = rawGaps.map((gap, i) => ({
                  topic: typeof gap === 'string' ? gap : (gap?.topic || gap?.title || gap?.gap || `Gap ${i + 1}`),
                  score: gap?.score || gap?.opportunity_score || (90 - i * 5),
                  reason: typeof gap?.reason === 'string' ? gap.reason : 
                    (gap?.description || gap?.why_important || `Identified gap in competitor coverage`)
                }));
                
                setAnalysis({
                  ...report,
                  strategic_briefing: report.counter_strategy?.executive_summary || 
                    `I have analyzed ${report.competitors_analyzed || 0} competitors in your space. I found ${contentGaps.length} content gaps and ${rawOpportunities.length} strategic opportunities for you to exploit.`,
                  key_insights: keyInsights,
                  recommended_actions: recommendedActions,
                  total_gaps: contentGaps.length,
                  opportunities_count: rawOpportunities.length,
                  content_gaps: contentGaps,
                  overall_threat: report.swot_analysis?.threats?.length > 3 ? 'High' : 
                    report.swot_analysis?.threats?.length > 1 ? 'Medium' : 'Low'
                });
                
                const comps = report.competitor_scan?.competitors || [];
                setCompetitors(comps.map((c, i) => ({
                  id: i,
                  name: c.name || c.domain || `Competitor ${i + 1}`,
                  domain: c.domain || c.url || '',
                  threat_level: c.threat_level || 'medium',
                  blog_posts: c.content_count || (50 - i * 10),
                  social_presence: c.social_score || (80 - i * 15),
                  content_gaps: c.gaps_found || (5 + i),
                  top_keywords: c.keywords || []
                })));
                
                break;
              }
            }
          }
        }
      } catch (err) {
        console.log('No previous analysis found:', err);
      }
    };
    
    fetchData();
    return () => { isMounted = false; };
  }, [API]);

  // Enhance gaps with scoring + generate ready-to-forge briefs
  useEffect(() => {
    const gaps = analysis?.content_gaps;
    if (!gaps || gaps.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/intelligence/enhance-gaps`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ gaps, competitor_url: competitors[0]?.domain || '' }),
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setEnhancedGaps(data.enhanced_gaps || []);
          setReadyBriefs(data.ready_to_forge_briefs || []);
          // Store into shared Intelligence context for cross-feature referencing
          storeCompetitorData({
            gaps: data.enhanced_gaps || [],
            content: competitors,
          });
        }
      } catch (err) {
        console.warn('Gap enhancement failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [analysis, competitors, API, storeCompetitorData]);

  const tabs = [
    { id: 'overview', label: 'Command Center', icon: '🎯' },
    { id: 'competitors', label: 'Enemy Intel', icon: '🎖️' },
    { id: 'gaps', label: 'Opportunities', icon: '💎' },
    { id: 'history', label: 'Mission Log', icon: '📜' }
  ];

  return (
    <div className="min-h-screen bg-[#050508] text-white overflow-x-hidden">
      {/* River Banner */}
      {workflow.active && workflow.targetTool === 'athena' && (
        <div className="relative z-50 px-6 pt-4"><RiverBanner /></div>
      )}
      {/* Ambient background - hidden on mobile to prevent overflow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="hidden md:block absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="hidden md:block absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>
      
      {/* Header */}
      <div className="relative border-b border-gray-800/50 bg-gray-900/30 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-5">
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <span className="text-2xl sm:text-3xl">🦉</span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-emerald-500 rounded-full border-2 border-gray-900" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">
                  <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                    Athena&apos;s War Room
                  </span>
                </h1>
                <p className="text-xs sm:text-sm text-gray-500">Competitive Intelligence Command Center</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="px-3 sm:px-4 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors text-sm"
                >
                  ← Back
                </button>
              )}
              <button
                onClick={() => setShowConfig(true)}
                className="
                  px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-bold text-sm sm:text-base
                  bg-gradient-to-r from-cyan-500 to-purple-600
                  hover:from-cyan-400 hover:to-purple-500
                  shadow-lg shadow-cyan-500/25
                  transition-all duration-300 hover:scale-105
                  flex items-center gap-2
                "
              >
                <span className="animate-pulse">🔍</span>
                <span className="hidden sm:inline">New Reconnaissance</span>
                <span className="sm:hidden">Recon</span>
              </button>
            </div>
          </div>
          
          {/* Configuration Modal */}
          {showConfig && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center z-50 p-4 py-6 sm:py-4 overflow-y-auto" data-testid="athena-recon-modal-overlay">
              <div className="bg-gray-900 border border-amber-500/30 rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto my-auto" data-testid="athena-recon-modal">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <span>🦉</span> Configure Reconnaissance
                </h3>
                
                {/* Analysis Depth Selection */}
                <div className="mb-6">
                  <label className="text-sm text-gray-400 mb-2 block">Analysis Depth</label>
                  <div className="grid grid-cols-2 gap-3">
                    {ANALYSIS_DEPTHS.map(depth => (
                      <button
                        key={depth.id}
                        onClick={() => setSelectedDepth(depth.id)}
                        className={`
                          p-3 rounded-xl border transition-all text-left
                          ${selectedDepth === depth.id 
                            ? 'bg-amber-500/20 border-amber-500 text-white' 
                            : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'}
                        `}
                      >
                        <div className="font-semibold">{depth.name}</div>
                        <div className="text-xs text-amber-400">{depth.credits} credits</div>
                        <div className="text-xs opacity-70 mt-1">{depth.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Industry */}
                <div className="mb-4">
                  <label className="text-sm text-gray-400 mb-2 block">Industry</label>
                  <select 
                    value={industry} 
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
                  >
                    <option value="technology">Technology</option>
                    <option value="saas">SaaS</option>
                    <option value="ecommerce">E-commerce</option>
                    <option value="marketing">Marketing</option>
                    <option value="finance">Finance</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="education">Education</option>
                  </select>
                </div>
                
                {/* Actions */}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowConfig(false)}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowConfig(false);
                      handleRunAnalysis();
                    }}
                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold hover:from-amber-400 hover:to-orange-500 transition-all flex items-center justify-center gap-2"
                  >
                    <span>🚀</span>
                    Run ({ANALYSIS_DEPTHS.find(d => d.id === selectedDepth)?.credits} credits)
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Tabs */}
          <div className="flex gap-2 mt-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-300
                  ${activeTab === tab.id 
                    ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/30' 
                    : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'}
                `}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {activeTab === 'overview' && (
          <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Left Column - Stats & Radar */}
            <div className="lg:col-span-2 space-y-6 sm:space-y-8">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {[
                  { icon: '🎯', value: competitors.length, label: 'Targets Tracked', color: 'cyan' },
                  { icon: '🔍', value: analysis?.total_gaps || 0, label: 'Gaps Found', color: 'purple' },
                  { icon: '💎', value: analysis?.opportunities_count || 0, label: 'Opportunities', color: 'emerald' },
                  { icon: '⚠️', value: typeof analysis?.overall_threat === 'string' ? analysis.overall_threat : 'N/A', label: 'Threat Level', color: 'amber' }
                ].map((stat, i) => (
                  <div 
                    key={i}
                    className="relative overflow-hidden bg-gray-900/50 backdrop-blur rounded-xl sm:rounded-2xl border border-gray-800 p-3 sm:p-5 hover:border-gray-700 transition-colors group"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br from-${stat.color}-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity`} />
                    <div className="relative">
                      <span className="text-xl sm:text-2xl">{stat.icon}</span>
                      <div className={`text-2xl sm:text-3xl font-bold text-${stat.color}-400 mt-1 sm:mt-2`}>
                        {typeof stat.value === 'object' ? JSON.stringify(stat.value) : stat.value}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Competitor Grid */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <span>🎖️</span> Enemy Intelligence
                  </h2>
                  <span className="text-sm text-gray-500">{competitors.length} targets</span>
                </div>
                
                {competitors.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {competitors.map((comp, i) => (
                      <CompetitorCard 
                        key={comp.id || i}
                        competitor={comp}
                        index={i}
                        isSelected={selectedCompetitor?.id === comp.id}
                        onSelect={setSelectedCompetitor}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-900/50 rounded-2xl border border-dashed border-gray-700 p-12 text-center">
                    <RadarSweep size={120} />
                    <h3 className="text-lg font-semibold text-gray-400 mt-6 mb-2">No Targets Acquired</h3>
                    <p className="text-gray-500 mb-4">Launch a reconnaissance mission to identify competitors</p>
                    <button className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors">
                      Start Reconnaissance →
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Column - Athena's Briefing */}
            <div>
              <AthenasBriefing analysis={analysis} isAnalyzing={isAnalyzing} />
            </div>
          </div>
        )}
        
        {activeTab === 'gaps' && (
          <OpportunityMatrix gaps={enhancedGaps || analysis?.content_gaps || []} briefs={readyBriefs} />
        )}
        
        {activeTab === 'competitors' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {competitors.length > 0 ? (
              competitors.map((comp, i) => (
                <CompetitorCard 
                  key={comp.id || i}
                  competitor={comp}
                  index={i}
                  isSelected={selectedCompetitor?.id === comp.id}
                  onSelect={setSelectedCompetitor}
                />
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <div className="text-4xl mb-4">🔍</div>
                <p className="text-gray-400">No competitor data yet. Run an analysis to populate this section.</p>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'history' && (
          <div className="bg-gray-900/50 rounded-2xl border border-gray-800 p-8 text-center">
            <div className="text-4xl mb-4">📜</div>
            <h3 className="text-xl font-bold text-white mb-2">Mission History</h3>
            <p className="text-gray-400">Coming soon - Track all your reconnaissance missions</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AthenaWarRoom;
